'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X } from 'lucide-react';
import { useBrandLogos } from '@/hooks/useBrandLogos';

export default function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logoWordmarkUrl } = useBrandLogos();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-md border-b' : 'bg-transparent'
      }`}
      style={scrolled ? { backgroundColor: 'rgba(26,29,39,0.96)', borderColor: 'rgba(255,255,255,0.08)' } : {}}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link href="/landing-page" className="flex items-center gap-2">
          <AppLogo size={32} />
          {logoWordmarkUrl ? (
            <img src={logoWordmarkUrl} alt="Ovation" className="h-5 object-contain" />
          ) : (
            <span className="font-bold text-lg tracking-tight text-gradient-gold">Ovation</span>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link href="/landing-page#features" className="text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors">Features</Link>
          <Link href="/landing-page#search" className="text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors">Search</Link>
          <Link href="/landing-page#preview" className="text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors">Preview</Link>
          <Link href="/landing-page#programs" className="text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors">Popular</Link>
          <Link href="/landing-page#nearby" className="text-sm font-medium text-zinc-400 hover:text-zinc-100 transition-colors">Near Me</Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-semibold text-zinc-300 hover:text-zinc-100 transition-colors px-4 py-2"
          >
            Sign In
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-semibold bg-gold-500 hover:bg-gold-400 text-zinc-950 px-4 py-2 rounded-lg transition-all duration-150 active:scale-95"
          >
            Get Started Free
          </Link>
        </div>

        <button
          className="md:hidden text-zinc-400 hover:text-zinc-100 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-b px-6 py-4 flex flex-col gap-4 animate-slide-up" style={{ backgroundColor: 'rgba(26,29,39,0.98)', borderColor: 'rgba(255,255,255,0.08)' }}>
          <Link href="/landing-page#features" className="text-sm font-medium text-zinc-300" onClick={() => setMobileOpen(false)}>Features</Link>
          <Link href="/landing-page#search" className="text-sm font-medium text-zinc-300" onClick={() => setMobileOpen(false)}>Search</Link>
          <Link href="/landing-page#preview" className="text-sm font-medium text-zinc-300" onClick={() => setMobileOpen(false)}>Preview</Link>
          <Link href="/landing-page#programs" className="text-sm font-medium text-zinc-300" onClick={() => setMobileOpen(false)}>Popular</Link>
          <Link href="/landing-page#nearby" className="text-sm font-medium text-zinc-300" onClick={() => setMobileOpen(false)}>Near Me</Link>
          <Link href="/sign-up-login-screen" className="text-sm font-semibold text-gold-400" onClick={() => setMobileOpen(false)}>Sign In / Sign Up</Link>
        </div>
      )}
    </header>
  );
}